"use client";

import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import {
	RiAlertLine,
	RiEmotionUnhappyLine,
	RiMoreLine,
	RiSpamLine,
	RiUserUnfollowLine,
} from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@workspace/ui/components/form";
import { Spinner } from "@workspace/ui/components/spinner";
import { Textarea } from "@workspace/ui/components/textarea";
import { toast } from "@workspace/ui/hooks/use-sonner-toast";
import { memo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { trpc } from "@/lib/trpc/react";
import { cn } from "@/lib/utils";

const reportFormSchema = z.object({
	reason: z.enum([
		"inappropriate",
		"spam",
		"harassment",
		"hate_speech",
		"other",
	]),
	additionalNotes: z.string().optional(),
});

type ReportFormValues = z.infer<typeof reportFormSchema>;

interface ReportContentDialogProps {
	isOpen: boolean;
	onClose: () => void;
	contentType: "set_review" | "show_review";
	contentId: string;
	reviewUsername?: string | null;
}

type ReportReason =
	| "inappropriate"
	| "spam"
	| "harassment"
	| "hate_speech"
	| "other";

const reportReasons = [
	{
		value: "inappropriate" as ReportReason,
		label: "Inappropriate content",
		icon: RiAlertLine,
	},
	{
		value: "spam" as ReportReason,
		label: "Spam or misleading",
		icon: RiSpamLine,
	},
	{
		value: "harassment" as ReportReason,
		label: "Harassment or bullying",
		icon: RiUserUnfollowLine,
	},
	{
		value: "hate_speech" as ReportReason,
		label: "Hate speech",
		icon: RiEmotionUnhappyLine,
	},
	{
		value: "other" as ReportReason,
		label: "Other",
		icon: RiMoreLine,
	},
];

export const ReportContentDialog = memo<ReportContentDialogProps>(
	({ isOpen, onClose, contentType, contentId, reviewUsername }) => {
		const submitReportMutation = trpc.reports.submit.useMutation({
			onSuccess: () => {
				toast({
					title: "Report submitted",
					description:
						"Thank you for helping keep our community safe. We'll review this report promptly.",
				});
				onClose();
			},
			onError: (error) => {
				toast({
					title: "Error",
					description: `Failed to submit report. ${error.message}`,
					variant: "destructive",
				});
			},
		});

		const form = useForm<ReportFormValues>({
			resolver: standardSchemaResolver(reportFormSchema),
			defaultValues: {
				reason: "inappropriate",
				additionalNotes: "",
			},
		});

		useEffect(() => {
			if (isOpen) {
				form.reset({
					reason: "inappropriate",
					additionalNotes: "",
				});
			}
		}, [isOpen, form]);

		const handleSubmit = useCallback(
			async (data: ReportFormValues) => {
				submitReportMutation.mutate({
					contentType,
					contentId,
					reason: data.reason,
					additionalNotes: data.additionalNotes,
				});
			},
			[submitReportMutation, contentType, contentId],
		);

		return (
			<Dialog open={isOpen} onOpenChange={onClose}>
				<DialogContent className="max-h-[90vh] w-full max-w-[95%] overflow-auto p-0 sm:max-w-md">
					<DialogTitle className="sr-only">Report Content</DialogTitle>
					<DialogDescription className="sr-only">
						Report inappropriate content to help keep our community safe.
					</DialogDescription>
					<DialogHeader className="px-6 pt-6 pb-0">
						<h2 className="font-semibold leading-none tracking-tight">
							Report Content
						</h2>
						<p className="text-muted-foreground text-sm">
							{reviewUsername
								? `Report review by @${reviewUsername}`
								: "Help us understand what's wrong with this content"}
						</p>
					</DialogHeader>

					<div className="px-6 pb-6">
						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(handleSubmit)}
								className="space-y-6"
							>
								<FormField
									control={form.control}
									name="reason"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Select a reason</FormLabel>
											<div className="space-y-2">
												{reportReasons.map((reason) => (
													<button
														key={reason.value}
														type="button"
														onClick={() => field.onChange(reason.value)}
														className={cn(
															"flex w-full items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors",
															field.value === reason.value
																? "border-primary bg-primary/5"
																: "border-muted bg-background hover:bg-accent hover:text-accent-foreground",
														)}
													>
														<reason.icon
															className={cn(
																"h-5 w-5",
																field.value === reason.value
																	? "text-primary"
																	: "text-muted-foreground",
															)}
														/>
														<span className="text-sm font-medium">
															{reason.label}
														</span>
													</button>
												))}
											</div>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="additionalNotes"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Additional details (optional)</FormLabel>
											<FormControl>
												<Textarea
													placeholder="Provide more context about this report..."
													rows={3}
													maxLength={500}
													{...field}
												/>
											</FormControl>
											<div className="text-right text-muted-foreground text-xs">
												{field.value?.length || 0}/500
											</div>
											<FormMessage />
										</FormItem>
									)}
								/>

								<DialogFooter>
									<div className="flex w-full items-center justify-between">
										<div />
										<div className="flex gap-2">
											<Button variant="ghost" onClick={onClose} type="button">
												Cancel
											</Button>
											<Button
												type="submit"
												variant="destructive"
												disabled={submitReportMutation.isPending}
											>
												{submitReportMutation.isPending ? (
													<div className="flex items-center gap-2">
														<Spinner size="sm" variant="current" />
														<span>Submitting...</span>
													</div>
												) : (
													"Submit Report"
												)}
											</Button>
										</div>
									</div>
								</DialogFooter>
							</form>
						</Form>
					</div>
				</DialogContent>
			</Dialog>
		);
	},
);

ReportContentDialog.displayName = "ReportContentDialog";
